import { FILE_SOURCE_TYPES } from "@/Features/Files/files.types";
import { isEvidencePhoto } from "@/Features/EvidencePhotos/evidence-photos.utils";
import { evidenceDetailPath } from "@/Features/Evidence/evidenceLinks";

type SourceFields = {
  source_type?: string | null;
};

/**
 * The Evidence Files folder or an evidence item's folder: Monolith creates,
 * names and deletes these, so users can't rename, delete, move or create
 * folders in them (the Files API answers 403, ENG-2089 and ENG-2130).
 */
export const isManagedFolder = (row?: SourceFields | null) =>
  row?.source_type === FILE_SOURCE_TYPES.EVIDENCE_ROOT_FOLDER ||
  row?.source_type === FILE_SOURCE_TYPES.EVIDENCE_ITEM_FOLDER;

/**
 * A file in an evidence item's folder. Every file there is tagged
 * `evidence_photo`, images or not. It can be renamed, described and deleted,
 * but not moved out of its item's folder.
 */
export const isEvidenceFile = (row?: SourceFields | null) =>
  row?.source_type === FILE_SOURCE_TYPES.EVIDENCE_PHOTO;

/** Managed rows can't be dragged anywhere. */
export const isManagedRow = (row?: SourceFields | null) =>
  isManagedFolder(row) || isEvidenceFile(row);

/**
 * Whether a drop onto `target` (a folder row, a breadcrumb, or the folder
 * being browsed) goes through. Moving a row into the managed tree is always
 * refused. A desktop file drop is an upload: allowed into an item folder
 * (ENG-1446), refused at the Evidence Files root.
 */
export const canDropInto = (
  target: SourceFields | null | undefined,
  { internal }: { internal: boolean }
) => {
  if (internal) return !isManagedFolder(target);
  return target?.source_type !== FILE_SOURCE_TYPES.EVIDENCE_ROOT_FOLDER;
};

export const managedFolderMessage = (action: string) =>
  `This is a system managed folder and can't be ${action}.`;

export const MANAGED_NEW_FOLDER_MESSAGE =
  "This is a system managed folder, so new folders can't be created in it.";

/** "photo" for images, "file" for anything else in an item's folder. */
export const evidenceFileNoun = (file: { name: string }) =>
  isEvidencePhoto(file) ? "photo" : "file";

/** "evidence item 001", or "an evidence item" while the number is unknown. */
export const evidenceItemLabel = (evidenceNumber?: string | null) =>
  evidenceNumber ? `evidence item ${evidenceNumber}` : "an evidence item";

/** The evidence item's Files tab, opened from Case Files. */
export const evidenceItemFilesPath = (uuid: string, caseId?: number | null) =>
  evidenceDetailPath(uuid, { tab: "files", caseId });

/**
 * The evidence delete confirm's line about what goes with the item, from its
 * `file_count` (every file in its folder, images or not). Null with none.
 */
export const evidenceDeleteFilesMessage = (count?: number | null) => {
  if (!count || count < 1) return null;
  return `This also deletes its ${count} ${count === 1 ? "file" : "files"}.`;
};
